/* Version: 3.5.155 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useContext, useEffect, useMemo, useCallback } from 'react';
import { ButtonRightIcon } from '@manulife/mux-cds-icons';
import PropTypes from 'prop-types';
import { SVG_ICON_SIZE_DEFAULT } from '../Icon/constants.js';
import { BREAKPOINTS } from '../../spacing/index.js';
import { safeRest, rem, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import SideNavContext from './SideNavContext.js';
import { MainItem, MainItemComponent, MainNavItemIconWrapper, MainNavItemLabel } from './styledComponents.js';
import SubNav from './SubNav.js';
import { hasActiveChild, getNavItemProps, isOverlay } from './utils.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';
import useMedia from '../../hooks/useMedia.js';

var _excluded = ["id", "as", "label", "itemKey", "icon", "to", "onClick", "children"];
var MainNavItem = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var controlledId = _ref.id,
    _ref$as = _ref.as,
    as = _ref$as === undefined ? 'a' : _ref$as,
    label = _ref.label,
    itemKey = _ref.itemKey,
    icon = _ref.icon,
    to = _ref.to,
    _ref$onClick = _ref.onClick,
    onClick = _ref$onClick === undefined ? noop : _ref$onClick,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId(), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var _useContext = useContext(SideNavContext),
    keepOpenOnSelect = _useContext.keepOpenOnSelect,
    isWideMainNav = _useContext.isWideMainNav,
    activeKey = _useContext.activeKey,
    activeParentKey = _useContext.activeParentKey,
    expandedParentKey = _useContext.expandedParentKey,
    handleNavItemClick = _useContext.handleNavItemClick,
    setActiveParentKey = _useContext.setActiveParentKey,
    setExpandedParentKey = _useContext.setExpandedParentKey;
  var showHamburgerMenu = useMedia(["(max-width: ".concat(BREAKPOINTS.MAX_1024, "px)")],
  // media queries
  [true],
  // values corresponding to media query
  false // default value
  );
  var isActive = activeKey === itemKey;
  var isActiveParent = activeParentKey === itemKey;
  var isExpanded = expandedParentKey === itemKey;
  useEffect(function () {
    if (hasActiveChild(children, activeKey) || activeKey === itemKey) {
      setActiveParentKey(itemKey);
      if (keepOpenOnSelect && !showHamburgerMenu && children) {
        setExpandedParentKey(itemKey);
      }
    }
    // only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var isSelected = isActive || isActiveParent || isExpanded;
  var isOtherParentSelected = !isActiveParent && !!activeParentKey;
  var component = showHamburgerMenu && !!children ? 'button' : as;
  var mainNavItemProps = getNavItemProps({
    component: component,
    to: to,
    hasChildren: !!children,
    isExpanded: isExpanded
  });
  var iconStyle = useMemo(function () {
    var opacity = theme.sideNav.mainNav.iconOpacity;
    if (showHamburgerMenu || isSelected || !isSelected && !isOtherParentSelected) {
      opacity = 1;
    }
    return {
      size: rem(SVG_ICON_SIZE_DEFAULT),
      opacity: opacity
    };
  }, [showHamburgerMenu, isSelected, isOtherParentSelected, theme]);
  var handleClick = useCallback(function (e) {
    if (!children || to && !showHamburgerMenu) {
      handleNavItemClick(itemKey);
      setActiveParentKey(itemKey);
    }
    // set current itemKey as expanded key if item has children and is not the current expanded key
    setExpandedParentKey(!!children && !isExpanded ? itemKey : undefined);
    handle(onClick)(e);
  }, [isExpanded, itemKey, to, onClick, handleNavItemClick, setActiveParentKey, setExpandedParentKey, children, showHamburgerMenu]);
  return /*#__PURE__*/jsxs(MainItem, {
    theme: theme,
    children: [/*#__PURE__*/jsxs(MainItemComponent, _objectSpread(_objectSpread(_objectSpread({}, mainNavItemProps), {}, {
      id: id,
      as: component,
      "data-selected": isSelected,
      "data-testid": "main-item-".concat(id),
      onClick: handleClick,
      ref: ref,
      theme: theme,
      $hasIcon: !!icon,
      $isActive: isSelected
    }, safeProps), {}, {
      children: [icon && /*#__PURE__*/jsx(MainNavItemIconWrapper, {
        "data-testid": "main-icon-".concat(id),
        children: /*#__PURE__*/React.cloneElement(icon, {
          width: iconStyle.size,
          height: iconStyle.size,
          opacity: iconStyle.opacity,
          'aria-hidden': true
        })
      }), /*#__PURE__*/jsx(MainNavItemLabel, {
        isWideMainNav: isWideMainNav,
        theme: theme,
        children: label
      }), showHamburgerMenu && isOverlay(theme) && children && /*#__PURE__*/jsx(ButtonRightIcon, {
        fontSize: rem(SVG_ICON_SIZE_DEFAULT)
      })]
    })), children && /*#__PURE__*/jsx(SubNav, {
      id: id,
      backButtonLabel: label,
      isSubNavExpanded: isExpanded,
      children: children
    })]
  });
});
process.env.NODE_ENV !== "production" ? MainNavItem.propTypes = {
  /** id for MainNavItem */
  id: PropTypes.string,
  /** type of component to use as nav item (e.g. Link from react-router-dom, button, etc.) */
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** label for MainNavItem */
  label: PropTypes.node,
  /** unique key used for item selection */
  itemKey: PropTypes.string.isRequired,
  /** icon for MainNavItem */
  icon: PropTypes.node,
  /** url (by default maps to href) or url/object/function (when using Link from react-router-dom) */
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func]),
  /** onClick handler for item */
  onClick: PropTypes.func,
  /** 1 or more SubNavItem or SubNavDropdown components */
  children: PropTypes.node
} : undefined;
process.env.NODE_ENV !== "production" ? MainNavItem.displayName = 'MainNavItem' : undefined;

export { MainNavItem as default };
